/**
 * IndexedDB schema for offline-first POS.
 *
 * STORES:
 *   outbox            — pending actions to sync (sales, expenditures, etc.)
 *   cache_products    — reference-data cache for offline reads
 *   cache_customers   — reference-data cache for offline reads
 *   meta              — bookkeeping (last sync timestamps, schema version)
 *
 * SCHEMA VERSIONING:
 *   v1 (this release) — initial layout.
 *   When you bump SCHEMA_VERSION, add a case to the upgrade callback.
 *   IndexedDB upgrades run in a versionchange transaction — keep them fast.
 *
 * DEPENDENCIES:
 *   This file uses `idb` (https://www.npmjs.com/package/idb). Add it to
 *   eaze-software with:
 *
 *     npm install idb
 *
 *   It's ~3kB gzipped, native IndexedDB wrapper from Jake Archibald.
 */
import { openDB, type DBSchema, type IDBPDatabase } from "idb";

export const DB_NAME = "eaze";
export const SCHEMA_VERSION = 1;

// ── Type definitions ────────────────────────────────────────────

/** Status lifecycle for outbox items. */
export type OutboxStatus =
  | "pending" // waiting for next sync attempt
  | "in_flight" // currently being sent
  | "failed" // last attempt failed; will retry per backoff schedule
  | "conflict" // server returned 409 — needs operator resolution
  | "done"; // successfully synced; safe to garbage-collect

/** Action types — extend as new offline-capable operations are added. */
export type OutboxActionType =
  | "order.create"
  | "order.update"
  | "expenditure.create"
  | "customer.create"
  | "stock.adjust"
  | "payment.record";

export interface OutboxItem {
  /** Globally unique. Also used as the server-side idempotencyKey. */
  id: string;
  type: OutboxActionType;
  /** Server endpoint (relative path, e.g. "/orders"). */
  endpoint: string;
  method: "POST" | "PATCH" | "DELETE" | "PUT";
  /** Request body. Will be sent as JSON. */
  payload: Record<string, unknown>;
  /** Scope — used to filter the queue per current user / business. */
  companyId: string;
  businessId: string;
  userId: string | null;
  /** Human-readable one-line summary for the UI ("Cash sale 125.00 · Linda"). */
  summary: string;
  /** Lifecycle. */
  status: OutboxStatus;
  attempts: number;
  /** Last error message from server, if any. */
  lastError: string | null;
  /** Last error response body (e.g. 409 detail). */
  lastErrorDetail: Record<string, unknown> | null;
  /** Unix ms. */
  createdAt: number;
  updatedAt: number;
  /** When to retry next (null = retry immediately, otherwise wait until this ms). */
  nextRetryAt: number | null;
  /** Server response on success — saved for confirmation UI. */
  serverResponse: Record<string, unknown> | null;
}

export interface CachedProduct {
  _id: string;
  companyId: string;
  businessId: string;
  name: string;
  sku?: string | null;
  price: number;
  quantity: number;
  /** Whatever else the POS needs to render. */
  [key: string]: unknown;
  /** When this row was last refreshed. */
  cachedAt: number;
}

export interface CachedCustomer {
  _id: string;
  companyId: string;
  businessId: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  normalizedEmail?: string | null;
  normalizedPhone?: string | null;
  [key: string]: unknown;
  cachedAt: number;
}

export interface MetaEntry {
  key: string;
  value: unknown;
  updatedAt: number;
}

// ── DB schema declaration ────────────────────────────────────────

interface EazeDB extends DBSchema {
  outbox: {
    key: string;
    value: OutboxItem;
    indexes: {
      "by-status": string;
      "by-scope": string; // `${companyId}:${businessId}`
      "by-created": number;
      "by-next-retry": number;
    };
  };
  cache_products: {
    key: string;
    value: CachedProduct;
    indexes: {
      "by-business": string;
      "by-sku": string;
    };
  };
  cache_customers: {
    key: string;
    value: CachedCustomer;
    indexes: {
      "by-business": string;
      "by-email": string;
      "by-phone": string;
    };
  };
  meta: {
    key: string;
    value: MetaEntry;
  };
}

// ── Open ─────────────────────────────────────────────────────────

let dbPromise: Promise<IDBPDatabase<EazeDB>> | null = null;

export function getDB(): Promise<IDBPDatabase<EazeDB>> {
  if (!dbPromise) {
    dbPromise = openDB<EazeDB>(DB_NAME, SCHEMA_VERSION, {
      upgrade(db, oldVersion) {
        // Version 1: initial layout
        if (oldVersion < 1) {
          const outbox = db.createObjectStore("outbox", { keyPath: "id" });
          outbox.createIndex("by-status", "status");
          outbox.createIndex("by-scope", "scopeKey" as never); // synthesized at write time
          outbox.createIndex("by-created", "createdAt");
          outbox.createIndex("by-next-retry", "nextRetryAt");

          const products = db.createObjectStore("cache_products", {
            keyPath: "_id",
          });
          products.createIndex("by-business", "businessId");
          products.createIndex("by-sku", "sku");

          const customers = db.createObjectStore("cache_customers", {
            keyPath: "_id",
          });
          customers.createIndex("by-business", "businessId");
          customers.createIndex("by-email", "normalizedEmail");
          customers.createIndex("by-phone", "normalizedPhone");

          db.createObjectStore("meta", { keyPath: "key" });
        }

        // Future migrations: if (oldVersion < 2) { ... }
      },
      blocked() {
        // Another tab holds an older version of this DB. Tell the user
        // to close other tabs.
        console.warn(
          "[offline-db] another tab has the database open at an older version",
        );
      },
      blocking() {
        // We have the DB open and a newer version wants to upgrade.
        // Close our connection to let the upgrade proceed.
        console.warn("[offline-db] closing so a newer version can upgrade");
      },
      terminated() {
        // The browser killed our connection (memory pressure, etc.).
        // Re-establish next time getDB() is called.
        dbPromise = null;
        console.warn("[offline-db] connection terminated; will reconnect");
      },
    });
  }
  return dbPromise;
}

// ── Helpers ──────────────────────────────────────────────────────

export function scopeKey(companyId: string, businessId: string): string {
  return `${companyId}:${businessId}`;
}

/** Generate a UUID for outbox items + idempotency keys. */
export function generateId(): string {
  // Prefer the modern Web Crypto API when available
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  // Fallback for old browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Wipe everything. Useful on logout. */
export async function clearAll(): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(
    ["outbox", "cache_products", "cache_customers", "meta"],
    "readwrite",
  );
  await Promise.all([
    tx.objectStore("outbox").clear(),
    tx.objectStore("cache_products").clear(),
    tx.objectStore("cache_customers").clear(),
    tx.objectStore("meta").clear(),
    tx.done,
  ]);
}
